import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  User,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { 
  collection, 
  getDocs, 
  doc, 
  updateDoc, 
  query,
  orderBy,
  getDoc,
  setDoc,
  deleteDoc,
  onSnapshot,
  addDoc,
  Timestamp
} from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';
import { generateSecret, verifyTOTP } from '@/lib/totp';
import { SEO } from '@/components/SEO';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  Users, 
  Crown, 
  Shield, 
  Search, 
  Download, 
  RefreshCw, 
  Building2, 
  Briefcase, 
  FileText, 
  LogOut, 
  Eye, 
  UserCheck, 
  CheckCircle2, 
  Clock, 
  XCircle, 
  AlertCircle,
  TrendingUp,
  Globe,
  Monitor,
  Navigation,
  Activity,
  BookOpen,
  Plus,
  Trash2,
  Edit,
  Upload,
  Loader2,
  ImageIcon,
  Ban,
  MessageSquare,
  Send,
  Lock,
  X
} from 'lucide-react';
import { format } from 'date-fns';
import { formatCurrency, compressBlogImage, getSafeBlogImageUrl } from '@/lib/utils';
import type { Invoice, UserProfile, Business, Customer, SupportTicket, TicketReply } from '@/types/invoice';
import { blogPosts as initialBlogPosts, type BlogPost } from '@/data/blogPosts';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface VisitLog {
  id: string;
  visitorId: string;
  userId: string;
  userEmail: string | null;
  path: string;
  referrer: string;
  browser: string;
  os: string;
  country: string;
  timestamp: Date;
}

interface AnonymousCreation {
  id: string;
  visitorId: string;
  type: 'invoice' | 'quotation';
  action: 'print' | 'download';
  currency: string;
  totalAmount: number;
  browser: string;
  os: string;
  country: string;
  timestamp: Date;
}

export default function Admin() {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Auth state
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  
  // Login form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  
  // Navigation / Tabs state
  const [activeTab, setActiveTab] = useState<'users' | 'analytics' | 'anonymous' | 'security' | 'blog' | 'tickets'>('users');

  // 2FA state
  const [mfaSecret, setMfaSecret] = useState<string | null>(null);
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [is2FaVerified, setIs2FaVerified] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);
  const [setupStep, setSetupStep] = useState<'idle' | 'scan' | 'verify'>('idle');
  const [totpError, setTotpError] = useState<string | null>(null);
  
  // Users state
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Invoices & document metrics state
  const [statsExtra, setStatsExtra] = useState({
    invoicesCount: 0,
    businessesCount: 0,
    customersCount: 0,
  });
  
  // Traffic analytics state
  const [visits, setVisits] = useState<VisitLog[]>([]);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  
  // Anonymous creations state
  const [anonymousCreations, setAnonymousCreations] = useState<AnonymousCreation[]>([]);
  const [loadingAnonymous, setLoadingAnonymous] = useState(false);
  
  // Public Landing Page Stats Settings state
  const [showFakeStats, setShowFakeStats] = useState(false);
  const [fakeInvoicesCount, setFakeInvoicesCount] = useState(15000);
  const [fakeUsersCount, setFakeUsersCount] = useState(1200);
  const [fakeCountriesCount, setFakeCountriesCount] = useState(50);
  const [homePageVersion, setHomePageVersion] = useState<'v1' | 'v2'>('v1');
  const [loadingStatsSettings, setLoadingStatsSettings] = useState(false);
  const [savingStatsSettings, setSavingStatsSettings] = useState(false);
  
  // User detail state
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [selectedUserInvoices, setSelectedUserInvoices] = useState<Invoice[]>([]);
  const [selectedUserBusinesses, setSelectedUserBusinesses] = useState<Business[]>([]);
  const [selectedUserCustomers, setSelectedUserCustomers] = useState<Customer[]>([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);

  // Blog management state
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [loadingBlog, setLoadingBlog] = useState(false);
  const [loadingBlogSave, setLoadingBlogSave] = useState(false);
  const [showBlogDialog, setShowBlogDialog] = useState(false);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [blogForm, setBlogForm] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    category: 'Invoicing',
    author: 'QuickFreeInvoice Team',
    tagsString: '',
    image: '',
  });
  
  // Support tickets management state
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  const [selectedTicketReplies, setSelectedTicketReplies] = useState<TicketReply[]>([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [ticketsSubmitting, setTicketsSubmitting] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | 'open' | 'closed'>('open');
  const [adminReplyText, setAdminReplyText] = useState('');
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [ticketSearchQuery, setTicketSearchQuery] = useState('');
  const [ticketDateQuery, setTicketDateQuery] = useState('');

  const currentTicket = tickets.find(t => t.ticketId === selectedTicketId) || null;

  const fetchTickets = async () => {
    if (!db) return;
    setLoadingTickets(true);
    try {
      const q = query(collection(db, 'tickets'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const ticketsList = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
          ...data,
          ticketId: docSnap.id,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
        } as SupportTicket;
      });
      setTickets(ticketsList);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoadingTickets(false);
    }
  };

  const handleAdminSubmitReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTicketId || !adminReplyText.trim() || !currentUser || !db) return;

    setTicketsSubmitting(true);
    try {
      const text = adminReplyText.trim();
      setAdminReplyText('');

      await addDoc(collection(db, 'tickets', selectedTicketId, 'replies'), {
        senderId: currentUser.uid,
        senderEmail: currentUser.email || 'Admin',
        isAdmin: true,
        text,
        createdAt: Timestamp.now(),
      });

      await updateDoc(doc(db, 'tickets', selectedTicketId), {
        updatedAt: Timestamp.now(),
      });

      fetchTickets();
    } catch (error) {
      console.error('Error submitting admin reply:', error);
      toast({
        title: 'Reply Failed',
        description: 'Failed to post reply.',
        variant: 'destructive',
      });
    } finally {
      setTicketsSubmitting(false);
    }
  };

  const handleAdminCloseTicket = async (ticketId: string) => {
    if (!db) return;
    if (window.confirm('Are you sure you want to close this ticket?')) {
      setTicketsSubmitting(true);
      try {
        await updateDoc(doc(db, 'tickets', ticketId), {
          status: 'closed',
          updatedAt: Timestamp.now(),
        });
        toast({
          title: 'Ticket Closed',
          description: 'Ticket status has been set to closed.',
        });
        fetchTickets();
      } catch (error) {
        console.error('Error closing ticket:', error);
        toast({
          title: 'Error',
          description: 'Failed to close ticket.',
          variant: 'destructive',
        });
      } finally {
        setTicketsSubmitting(false);
      }
    }
  };
  
  const checkMfaStatus = async (uid: string) => {
    if (!db) return;
    setMfaLoading(true);
    try {
      const totpDoc = await getDoc(doc(db, 'users', uid, 'mfa_config', 'totp'));
      if (totpDoc.exists() && totpDoc.data().enabled) {
        setMfaEnabled(true);
        const sessionVerified = sessionStorage.getItem('admin_2fa_verified');
        if (sessionVerified === 'true') {
          setIs2FaVerified(true);
        } else {
          setIs2FaVerified(false);
        }
      } else {
        setMfaEnabled(false);
        setIs2FaVerified(true);
      }
    } catch (error) {
      console.error('Error checking MFA status:', error);
    } finally {
      setMfaLoading(false);
    }
  };

  // Track auth state
  useEffect(() => {
    if (!auth) {
      setAuthLoading(false);
      return;
    }
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (user) {
        if (user.email === 'pathusena@gmail.com') {
          setIsAdmin(true);
          checkMfaStatus(user.uid);
        } else {
          setIsAdmin(false);
          // Redirect non-admin logged-in users to home page
          navigate('/');
        }
      } else {
        setIsAdmin(false);
        setIs2FaVerified(false);
      }
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, [navigate]);

  // Fetch directory records
  const fetchAllUsers = async () => {
    if (!db) return;
    setLoadingUsers(true);
    try {
      const q = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const userList = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          ...data,
          createdAt: data.createdAt?.toDate(),
          subscription: {
            ...data.subscription,
            currentPeriodEnd: data.subscription?.currentPeriodEnd?.toDate(),
            cancelAt: data.subscription?.cancelAt?.toDate(),
          }
        } as UserProfile;
      });
      setUsers(userList);
      
      // Load user documents metrics in parallel
      fetchExtraStats(userList);
    } catch (error: any) {
      console.error('Error fetching users:', error);
      toast({
        title: 'Error',
        description: 'Failed to retrieve users directory.',
        variant: 'destructive',
      });
    } finally {
      setLoadingUsers(false);
    }
  };

  // Fetch document counts across all users
  const fetchExtraStats = async (userProfiles: UserProfile[]) => {
    if (!db) return;
    try {
      const promises = userProfiles.map(async (u) => {
        const [invs, bus, cust] = await Promise.all([
          getDocs(collection(db!, 'users', u.uid, 'invoices')),
          getDocs(collection(db!, 'users', u.uid, 'businesses')),
          getDocs(collection(db!, 'users', u.uid, 'customers')),
        ]);
        return {
          invSize: invs.size,
          busSize: bus.size,
          custSize: cust.size,
        };
      });
      
      const results = await Promise.all(promises);
      const totals = results.reduce(
        (acc, r) => ({
          invoicesCount: acc.invoicesCount + r.invSize,
          businessesCount: acc.businessesCount + r.busSize,
          customersCount: acc.customersCount + r.custSize,
        }),
        { invoicesCount: 0, businessesCount: 0, customersCount: 0 }
      );
      
      setStatsExtra(totals);
    } catch (e) {
      console.error('Error fetching extra stats:', e);
    }
  };

  // Fetch traffic logs
  const fetchAnalytics = async () => {
    if (!db) return;
    setLoadingAnalytics(true);
    try {
      const q = query(collection(db, 'analytics_visits'), orderBy('timestamp', 'desc'));
      const snapshot = await getDocs(q);
      
      const targetEmails = ['pathusena@gmail.com', 'pathumsenarat@gmail.com'];
      const logs: VisitLog[] = [];
      const deletePromises: Promise<void>[] = [];

      snapshot.docs.forEach((doc) => {
        const data = doc.data();
        const email = (data.userEmail || '').toLowerCase();
        
        if (targetEmails.includes(email)) {
          // Delete document in background
          deletePromises.push(deleteDoc(doc.ref));
        } else {
          logs.push({
            id: doc.id,
            visitorId: data.visitorId || 'unknown',
            userId: data.userId || 'anonymous',
            userEmail: data.userEmail || null,
            path: data.path || '/',
            referrer: data.referrer || 'Direct',
            browser: data.browser || 'Unknown',
            os: data.os || 'Unknown',
            country: data.country || 'Unknown',
            timestamp: data.timestamp?.toDate() || new Date(),
          });
        }
      });

      // Execute deletions in background
      if (deletePromises.length > 0) {
        Promise.all(deletePromises)
          .then(() => {
            console.log(`Successfully cleaned up ${deletePromises.length} admin/staff traffic logs.`);
          })
          .catch((e) => {
            console.error('Error deleting admin/staff traffic logs:', e);
          });
      }

      setVisits(logs);
    } catch (error: any) {
      console.error('Error fetching analytics visits:', error);
      toast({
        title: 'Analytics Error',
        description: 'Failed to retrieve visitor analytics logs.',
        variant: 'destructive',
      });
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const fetchStatsSettings = async () => {
    if (!db) return;
    setLoadingStatsSettings(true);
    try {
      const statsDoc = await getDoc(doc(db, 'publicStats', 'global'));
      if (statsDoc.exists()) {
        const data = statsDoc.data();
        setShowFakeStats(data.showFakeStats === true);
        setFakeInvoicesCount(data.fakeInvoicesCount ?? 15000);
        setFakeUsersCount(data.fakeUsersCount ?? 1200);
        setFakeCountriesCount(data.fakeCountriesCount ?? 50);
        setHomePageVersion(data.homePageVersion === 'v2' ? 'v2' : 'v1');
      }
    } catch (error) {
      console.error('Error fetching public stats settings:', error);
    } finally {
      setLoadingStatsSettings(false);
    }
  };

  const saveStatsSettings = async (showFake: boolean, invoices: number, users: number, countries: number, version: 'v1' | 'v2') => {
    if (!db) return;
    setSavingStatsSettings(true);
    try {
      await setDoc(doc(db, 'publicStats', 'global'), {
        showFakeStats: showFake,
        fakeInvoicesCount: Number(invoices) || 0,
        fakeUsersCount: Number(users) || 0,
        fakeCountriesCount: Number(countries) || 0,
        homePageVersion: version,
        lastUpdated: Timestamp.now()
      }, { merge: true });
      
      toast({
        title: 'Settings Saved',
        description: 'Landing page statistics display preferences updated successfully.',
      });
    } catch (error) {
      console.error('Error saving public stats settings:', error);
      toast({
        title: 'Error Saving Settings',
        description: 'Failed to update landing page statistics settings.',
        variant: 'destructive',
      });
    } finally {
      setSavingStatsSettings(false);
    }
  };

  const fetchAnonymousCreations = async () => {
    if (!db) return;
    setLoadingAnonymous(true);
    try {
      const q = query(collection(db, 'anonymous_creations'), orderBy('timestamp', 'desc'));
      const snapshot = await getDocs(q);
      const creations = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          visitorId: data.visitorId || 'unknown',
          type: data.type || 'invoice',
          action: data.action || 'download',
          currency: data.currency || 'USD',
          totalAmount: data.totalAmount || 0,
          browser: data.browser || 'Unknown',
          os: data.os || 'Unknown',
          country: data.country || 'Unknown',
          timestamp: data.timestamp?.toDate() || new Date(),
        } as AnonymousCreation;
      });
      setAnonymousCreations(creations);
    } catch (error) {
      console.error('Error fetching anonymous creations:', error);
      toast({
        title: 'Error',
        description: 'Failed to retrieve anonymous creations.',
        variant: 'destructive',
      });
    } finally {
      setLoadingAnonymous(false);
    }
  };

  const getFrequentAnonymousUsers = () => {
    const userGroups: Record<string, {
      visitorId: string;
      count: number;
      country: string;
      browser: string;
      os: string;
      lastActive: Date;
    }> = {};

    anonymousCreations.forEach((c) => {
      const vid = c.visitorId;
      if (!userGroups[vid]) {
        userGroups[vid] = {
          visitorId: vid,
          count: 0,
          country: c.country,
          browser: c.browser,
          os: c.os,
          lastActive: c.timestamp,
        };
      }
      userGroups[vid].count += 1;
      if (c.timestamp > userGroups[vid].lastActive) {
        userGroups[vid].lastActive = c.timestamp;
      }
    });

    return Object.values(userGroups).sort((a, b) => b.count - a.count);
  };

  // Listen to support tickets for admin
  useEffect(() => {
    if (!isAdmin || !db) return;

    setLoadingTickets(true);
    const q = query(collection(db, 'tickets'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const ticketsList = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
          ...data,
          ticketId: docSnap.id,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
        } as SupportTicket;
      });
      setTickets(ticketsList);
      setLoadingTickets(false);
    }, (error) => {
      console.error('Error listening to support tickets:', error);
      setLoadingTickets(false);
    });

    return () => unsubscribe();
  }, [isAdmin]);

  // Listen to replies of selected ticket for admin
  useEffect(() => {
    if (!selectedTicketId || !db) {
      setSelectedTicketReplies([]);
      return;
    }

    const q = query(
      collection(db, 'tickets', selectedTicketId, 'replies'),
      orderBy('createdAt', 'asc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const repliesList = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
          ...data,
          replyId: docSnap.id,
          createdAt: data.createdAt?.toDate(),
        } as TicketReply;
      });
      setSelectedTicketReplies(repliesList);
    }, (error) => {
      console.error('Error listening to replies:', error);
    });

    return () => unsubscribe();
  }, [selectedTicketId]);

  useEffect(() => {
    if (isAdmin) {
      if (activeTab === 'users') {
        fetchAllUsers();
      } else if (activeTab === 'analytics') {
        fetchAnalytics();
        fetchAnonymousCreations();
        fetchStatsSettings();
      } else if (activeTab === 'anonymous') {
        fetchAnonymousCreations();
      } else if (activeTab === 'blog') {
        fetchBlogPosts();
      } else if (activeTab === 'tickets') {
        fetchTickets();
      }
    }
  }, [isAdmin, activeTab]);

  const fetchBlogPosts = async () => {
    if (!db) return;
    setLoadingBlog(true);
    try {
      const snapshot = await getDocs(collection(db, 'blogPosts'));
      if (snapshot.empty) {
        console.log('Seeding initial blog posts to Firestore from Admin portal...');
        const promises = initialBlogPosts.map((post) => 
          setDoc(doc(db!, 'blogPosts', post.id), post)
        );
        await Promise.all(promises);
        setBlogPosts(initialBlogPosts);
      } else {
        const list = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            ...data,
            tags: data.tags || [],
          } as BlogPost;
        });
        list.sort((a, b) => b.date.localeCompare(a.date));
        setBlogPosts(list);
      }
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      toast({
        title: 'Error',
        description: 'Failed to retrieve blog posts.',
        variant: 'destructive',
      });
    } finally {
      setLoadingBlog(false);
    }
  };

  const handleCreateNewPost = () => {
    setEditingPost(null);
    setBlogForm({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      category: 'Invoicing',
      author: 'QuickFreeInvoice Team',
      tagsString: '',
      image: '',
    });
    setShowBlogDialog(true);
  };

  const handleEditPost = (post: BlogPost) => {
    setEditingPost(post);
    setBlogForm({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt,
      content: post.content,
      category: post.category,
      author: post.author,
      tagsString: (post.tags || []).join(', '),
      image: post.image || '',
    });
    setShowBlogDialog(true);
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImageUploading(true);
    try {
      const base64Image = await compressBlogImage(file);
      setBlogForm((prev) => ({ ...prev, image: base64Image }));
      toast({
        title: 'Image uploaded successfully',
        description: 'Blog post cover image has been updated.',
      });
    } catch (error) {
      console.error('Error compressing image:', error);
      toast({
        title: 'Upload Failed',
        description: 'Could not compress image. Try a smaller image.',
        variant: 'destructive',
      });
    } finally {
      setImageUploading(false);
    }
  };

  const handleDeleteBlogPost = async (id: string) => {
    if (!db) return;
    if (!confirm('Are you sure you want to delete this blog post?')) return;
    
    try {
      await deleteDoc(doc(db, 'blogPosts', id));
      toast({
        title: 'Post Deleted',
        description: 'The blog post has been deleted from Firestore.',
      });
      setBlogPosts((prev) => prev.filter((post) => post.id !== id));
    } catch (error) {
      console.error('Error deleting blog post:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete blog post.',
        variant: 'destructive',
      });
    }
  };

  const handleSaveBlogPost = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!db) return;
    
    const title = blogForm.title.trim();
    let slug = blogForm.slug.trim().toLowerCase();
    
    if (!title) {
      toast({ title: 'Error', description: 'Title is required.', variant: 'destructive' });
      return;
    }
    
    if (!slug) {
      slug = title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)+/g, '');
    } else {
      slug = slug
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)+/g, '');
    }
    
    const slugConflict = blogPosts.find(p => p.slug === slug && (!editingPost || p.id !== editingPost.id));
    if (slugConflict) {
      toast({
        title: 'Slug Conflict',
        description: `Another post already uses the slug "${slug}". Please change it.`,
        variant: 'destructive',
      });
      return;
    }

    setLoadingBlogSave(true);
    try {
      const wordsPerMinute = 200;
      const wordCount = blogForm.content.split(/\s+/).filter(Boolean).length;
      const readTimeMinutes = Math.max(1, Math.round(wordCount / wordsPerMinute));
      const readTime = `${readTimeMinutes} min read`;
      
      const tags = blogForm.tagsString
        .split(',')
        .map((t) => t.trim())
        .filter((t) => t.length > 0);
        
      const postDate = editingPost ? editingPost.date : new Date().toLocaleDateString('en-CA');
      const id = editingPost ? editingPost.id : `${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      
      const savedPost: BlogPost = {
        id,
        title,
        slug,
        excerpt: blogForm.excerpt.trim() || title,
        content: blogForm.content,
        category: blogForm.category.trim() || 'Invoicing',
        author: blogForm.author.trim() || 'QuickFreeInvoice Team',
        date: postDate,
        readTime,
        tags,
        image: blogForm.image || '',
      };
      
      await setDoc(doc(db, 'blogPosts', id), savedPost);
      
      toast({
        title: editingPost ? 'Post Updated' : 'Post Created',
        description: `Successfully saved "${title}".`,
      });
      
      await fetchBlogPosts();
      setShowBlogDialog(false);
    } catch (error) {
      console.error('Error saving blog post:', error);
      toast({
        title: 'Error',
        description: 'Failed to save blog post.',
        variant: 'destructive',
      });
    } finally {
      setLoadingBlogSave(false);
    }
  };

  const handleAdminLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!auth) {
      toast({ title: 'Error', description: 'Firebase Auth not configured', variant: 'destructive' });
      return;
    }
    
    const normalizedEmail = email.trim().toLowerCase();
    
    // Local development admin credentials bypass
    if ((window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') && 
        normalizedEmail === 'pathusena@gmail.com' && 
        password === '@InvoiceAdmin2026321') {
      setCurrentUser({ email: 'pathusena@gmail.com', uid: 'mock_admin' } as any);
      setIsAdmin(true);
      setIs2FaVerified(true);
      toast({ title: 'Welcome Admin (Local Bypass)', description: 'Successfully authenticated mock super user.' });
      setLoginLoading(false);
      return;
    }

    setLoginLoading(true);
    try {
      await signInWithEmailAndPassword(auth, normalizedEmail, password);
      toast({ title: 'Welcome Admin', description: 'Successfully authenticated super user.' });
    } catch (error: any) {
      console.warn('Sign in failed, checking for registration trigger...', error);
      if (normalizedEmail === 'pathusena@gmail.com' && password === '@InvoiceAdmin2026321') {
        toast({
          title: 'Account Already Exists',
          description: 'Your pathusena@gmail.com account is already registered as a Pro account. Please sign in using your existing personal Pro account password.',
          variant: 'destructive',
        });
      } else {
        if (normalizedEmail === 'pathusena@gmail.com') {
          toast({
            title: 'Login Failed',
            description: 'Invalid credentials. Please use your personal password (associated with your Pro account) to access the staff portal.',
            variant: 'destructive',
          });
        } else {
          toast({ title: 'Login Failed', description: 'Invalid administrative credentials.', variant: 'destructive' });
        }
      }
    } finally {
      setLoginLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    if (!auth) {
      toast({ title: 'Error', description: 'Firebase Auth not configured', variant: 'destructive' });
      return;
    }
    
    setLoginLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      
      if (user.email && user.email.toLowerCase() === 'pathusena@gmail.com') {
        toast({ title: 'Welcome Admin', description: 'Google authentication successful.' });
      } else {
        await signOut(auth);
        toast({
          title: 'Access Denied',
          description: 'This Google account is not authorized to access the Super Admin Dashboard.',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.error('Google Sign-In failed:', error);
      toast({
        title: 'Sign-In Failed',
        description: error.message || 'An error occurred during Google Sign-In.',
        variant: 'destructive',
      });
    } finally {
      setLoginLoading(false);
    }
  };

  const handleMfaVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!db || !currentUser) return;
    setMfaLoading(true);
    try {
      const totpDoc = await getDoc(doc(db, 'users', currentUser.uid, 'mfa_config', 'totp'));
      if (totpDoc.exists()) {
        const { secret } = totpDoc.data();
        const isValid = await verifyTOTP(secret, totpCode);
        if (isValid) {
          setIs2FaVerified(true);
          sessionStorage.setItem('admin_2fa_verified', 'true');
          setTotpCode('');
          toast({ title: 'Access Granted', description: 'Two-factor verification successful.' });
        } else {
          setTotpError('Invalid verification code. Please check your authenticator and try again.');
        }
      } else {
        setIs2FaVerified(true);
      }
    } catch (error: any) {
      console.error('Error verifying MFA code:', error);
      setTotpError('An error occurred during verification. Please try again.');
    } finally {
      setMfaLoading(false);
    }
  };

  const handleStartMfaSetup = () => {
    const secret = generateSecret();
    setMfaSecret(secret);
    setSetupStep('scan');
    setTotpCode('');
    setTotpError(null);
  };

  const handleCancelSetup = () => {
    setMfaSecret(null);
    setSetupStep('idle');
    setTotpCode('');
    setTotpError(null);
  };

  const handleConfirmMfaSetup = async () => {
    if (!db || !currentUser || !mfaSecret) return;
    setMfaLoading(true);
    try {
      const isValid = await verifyTOTP(mfaSecret, totpCode);
      if (isValid) {
        await setDoc(doc(db, 'users', currentUser.uid, 'mfa_config', 'totp'), {
          secret: mfaSecret,
          enabled: true,
          updatedAt: new Date()
        });
        setMfaEnabled(true);
        setIs2FaVerified(true);
        sessionStorage.setItem('admin_2fa_verified', 'true');
        setSetupStep('idle');
        setMfaSecret(null);
        setTotpCode('');
        toast({
          title: '2FA Enabled',
          description: 'Two-factor authentication has been successfully set up for your administrator login.',
        });
      } else {
        setTotpError('Verification code failed. Please check the timing and try again.');
      }
    } catch (error: any) {
      console.error('Error completing MFA setup:', error);
      toast({
        title: 'Error',
        description: 'Failed to save MFA configuration. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setMfaLoading(false);
    }
  };

  const handleDisableMfa = async () => {
    if (!db || !currentUser) return;
    if (!confirm('Are you sure you want to disable two-factor authentication? This will reduce your portal security.')) return;
    
    setMfaLoading(true);
    try {
      await deleteDoc(doc(db, 'users', currentUser.uid, 'mfa_config', 'totp'));
      setMfaEnabled(false);
      setIs2FaVerified(true);
      sessionStorage.removeItem('admin_2fa_verified');
      setTotpCode('');
      toast({
        title: '2FA Disabled',
        description: 'Two-factor authentication has been disabled for this account.',
      });
    } catch (error: any) {
      console.error('Error disabling MFA:', error);
      toast({
        title: 'Error',
        description: 'Failed to disable 2FA. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setMfaLoading(false);
    }
  };

  const handleLogout = async () => {
    if (!auth) return;
    try {
      await signOut(auth);
      setIsAdmin(false);
      setCurrentUser(null);
      setUsers([]);
      setVisits([]);
      setIs2FaVerified(false);
      sessionStorage.removeItem('admin_2fa_verified');
      toast({ title: 'Logged Out', description: 'Admin session ended.' });
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleTogglePlan = async (userProfile: UserProfile) => {
    if (!db) return;
    const newPlan = userProfile.plan === 'pro' ? 'free' : 'pro';
    const userRef = doc(db, 'users', userProfile.uid);
    
    try {
      await updateDoc(userRef, {
        plan: newPlan,
        'subscription.status': newPlan === 'pro' ? 'active' : 'none',
        'subscription.provider': newPlan === 'pro' ? 'stripe' : '',
      });
      
      toast({
        title: 'Plan Updated',
        description: `Successfully toggled ${userProfile.email} to ${newPlan.toUpperCase()}`,
      });
      
      setUsers((prev) =>
        prev.map((u) =>
          u.uid === userProfile.uid
            ? {
                ...u,
                plan: newPlan,
                subscription: {
                  ...u.subscription,
                  status: newPlan === 'pro' ? 'active' : 'none',
                },
              }
            : u
        )
      );

      if (selectedUser?.uid === userProfile.uid) {
        setSelectedUser((prev) => prev ? { ...prev, plan: newPlan } : null);
      }
    } catch (error: any) {
      console.error('Error toggling plan:', error);
      toast({ title: 'Error', description: 'Failed to update plan status.', variant: 'destructive' });
    }
  };

  const handleToggleBlock = async (userProfile: UserProfile) => {
    if (!db) return;
    
    if (userProfile.email.toLowerCase() === 'pathusena@gmail.com') {
      toast({
        title: 'Action Denied',
        description: 'You cannot block the primary administrator account.',
        variant: 'destructive',
      });
      return;
    }

    const newBlockedState = !userProfile.isBlocked;
    const userRef = doc(db, 'users', userProfile.uid);
    
    try {
      await updateDoc(userRef, {
        isBlocked: newBlockedState
      });
      
      toast({
        title: newBlockedState ? 'User Account Blocked' : 'User Account Unblocked',
        description: `Successfully ${newBlockedState ? 'blocked' : 'unblocked'} ${userProfile.email}`,
      });
      
      setUsers((prev) =>
        prev.map((u) =>
          u.uid === userProfile.uid
            ? {
                ...u,
                isBlocked: newBlockedState,
              }
            : u
        )
      );

      if (selectedUser?.uid === userProfile.uid) {
        setSelectedUser((prev) => prev ? { ...prev, isBlocked: newBlockedState } : null);
      }
    } catch (error: any) {
      console.error('Error toggling block status:', error);
      toast({ 
        title: 'Error', 
        description: 'Failed to update user block status.', 
        variant: 'destructive' 
      });
    }
  };


  const handleViewDetails = async (userProfile: UserProfile) => {
    if (!db) return;
    setSelectedUser(userProfile);
    setLoadingDetails(true);
    setShowDetailDialog(true);
    
    try {
      const invoicesSnapshot = await getDocs(collection(db, 'users', userProfile.uid, 'invoices'));
      const invoiceList = invoicesSnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          ...data,
          issueDate: data.issueDate?.toDate(),
          dueDate: data.dueDate?.toDate(),
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
        } as Invoice;
      });
      setSelectedUserInvoices(invoiceList);

      const businessesSnapshot = await getDocs(collection(db, 'users', userProfile.uid, 'businesses'));
      const businessList = businessesSnapshot.docs.map(doc => doc.data() as Business);
      setSelectedUserBusinesses(businessList);

      const customersSnapshot = await getDocs(collection(db, 'users', userProfile.uid, 'customers'));
      const customerList = customersSnapshot.docs.map(doc => doc.data() as Customer);
      setSelectedUserCustomers(customerList);

    } catch (error: any) {
      console.error('Error fetching user details:', error);
      toast({
        title: 'Error',
        description: 'Failed to retrieve subcollections.',
        variant: 'destructive',
      });
    } finally {
      setLoadingDetails(false);
    }
  };

  // Helper aggregate functions for Analytics
  const getTopFrequencies = (key: keyof VisitLog, excludeValue?: string) => {
    const counts: Record<string, number> = {};
    visits.forEach((v) => {
      const val = v[key] as string;
      if (val && val !== excludeValue) {
        counts[val] = (counts[val] || 0) + 1;
      }
    });
    return Object.entries(counts)
      .map(([value, count]) => ({ value, count }))
      .sort((a, b) => b.count - a.count);
  };

  // Aggregated analytics maps
  const uniqueVisitorsCount = new Set(visits.map(v => v.visitorId)).size;
  const topCountries = getTopFrequencies('country');
  const topBrowsers = getTopFrequencies('browser');
  const topOS = getTopFrequencies('os');
  const topReferrers = getTopFrequencies('referrer');
  const topFrequentUsers = getTopFrequencies('userEmail'); // Users grouped by email

  const totalUsers = users.length;
  const proUsers = users.filter(u => u.plan === 'pro').length;
  const freeUsers = totalUsers - proUsers;
  const todayStr = new Date().toLocaleDateString('en-CA');
  const totalDownloads = users.reduce((sum, u) => sum + (u.downloadCount || 0), 0);
  const todayDownloadsTotal = users.reduce((sum, u) => {
    const todayDownloads = u.lastDownloadDate === todayStr ? (u.dailyDownloadCount || 0) : 0;
    return sum + todayDownloads;
  }, 0);

  const filteredUsers = users.filter((u) => 
    (u.email || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (u.displayName || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getInvoiceStatusBadge = (status: string) => {
    const configs: Record<string, { icon: any; color: string }> = {
      paid:      { icon: CheckCircle2, color: 'bg-green-100 text-green-800 border-green-200' },
      sent:      { icon: Clock,        color: 'bg-blue-100 text-blue-800 border-blue-200' },
      draft:     { icon: AlertCircle,  color: 'bg-gray-100 text-gray-800 border-gray-200' },
      cancelled: { icon: XCircle,      color: 'bg-red-100 text-red-800 border-red-200' },
    };
    const config = configs[status] || configs.draft;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} flex items-center gap-1 w-fit border`}>
        <Icon className="h-3 w-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  // Auth portal view
  if (!currentUser || !isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-550 via-purple-500 to-indigo-900 px-4 py-12">
        <SEO title="Staff Portal" description="Administrative access portal." noindex={true} />
        <Card className="w-full max-w-md shadow-2xl border border-gray-150">
          <CardHeader className="text-center">
            <div className="mx-auto bg-primary/10 p-3 rounded-full w-fit mb-2">
              <Shield className="h-8 w-8 text-primary" />
            </div>
            <CardTitle className="text-2xl font-bold tracking-tight">Staff Portal</CardTitle>
            <CardDescription className="space-y-2">
              <span>Sign in with administrative credentials to access command tools.</span>
              {email.trim().toLowerCase() === 'pathusena@gmail.com' && (
                <div className="mt-2 text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-left">
                  ⚠️ <strong>Pro Account Detected:</strong> Since your email is already registered as a Pro user, please use your <strong>personal account password</strong> to sign in. The default admin passcode will not work.
                </div>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAdminLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Admin Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Passcode</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={loginLoading}>
                {loginLoading ? 'Authenticating...' : 'Sign In'}
              </Button>
            </form>

            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-muted-foreground font-medium">Or continue with</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full flex items-center justify-center gap-2 border-slate-200 hover:bg-slate-50 transition-colors"
              onClick={handleGoogleLogin}
              disabled={loginLoading}
            >
              <svg className="h-4 w-4 mr-1 shrink-0" viewBox="0 0 24 24" width="24" height="24" xmlns="http://www.w3.org/2000/svg">
                <g transform="matrix(1, 0, 0, 1, 0, 0)">
                  <path d="M21.35,11.1H12v2.7h5.38c-0.24,1.28 -0.96,2.37 -2.04,3.1v2.57h3.3c1.93,-1.78 3.04,-4.4 3.04,-7.47c0,-0.61 -0.05,-1.2 -0.15,-1.7C20.83,11.1 21.09,11.1 21.35,11.1z" fill="#4285F4" />
                  <path d="M12,20.73c2.43,0 4.47,-0.8 5.96,-2.2l-2.92,-2.27c-0.81,0.54 -1.85,0.87 -3.04,0.87c-2.34,0 -4.32,-1.58 -5.02,-3.7H3.97v2.33c1.5,2.98 4.6,4.97 8.03,4.97z" fill="#34A853" />
                  <path d="M6.98,13.43c-0.18,-0.54 -0.28,-1.11 -0.28,-1.7c0,-0.59 0.1,-1.16 0.28,-1.7V7.7H3.97c-0.63,1.26 -0.97,2.67 -0.97,4.17c0,1.5 0.35,2.91 0.97,4.17L6.98,13.43z" fill="#FBBC05" />
                  <path d="M12,6.57c1.32,0 2.5,0.45 3.44,1.35l2.58,-2.58C16.46,3.84 14.42,3 12,3C8.57,3 5.47,4.99 3.97,7.7l3.01,2.33C7.68,8.15 9.66,6.57 12,6.57z" fill="#EA4335" />
                </g>
              </svg>
              Google
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // 2FA verification gate view
  if (currentUser && isAdmin && !is2FaVerified) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-550 via-purple-500 to-indigo-900 px-4 py-12">
        <SEO title="Staff Portal - Verification" description="Administrative verification gate." noindex={true} />
        <Card className="w-full max-w-md shadow-2xl border border-gray-150">
          <CardHeader className="text-center">
            <div className="mx-auto bg-primary/10 p-3 rounded-full w-fit mb-2">
              <Shield className="h-8 w-8 text-primary animate-pulse" />
            </div>
            <CardTitle className="text-2xl font-bold tracking-tight">Two-Factor Authentication</CardTitle>
            <CardDescription>
              Enter the 6-digit verification code from your Google Authenticator app.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleMfaVerify} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="mfaCode" className="text-sm font-medium">Verification Code</Label>
                <Input
                  id="mfaCode"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="000000"
                  className="text-center text-2xl font-mono tracking-widest"
                  value={totpCode}
                  onChange={(e) => {
                    setTotpCode(e.target.value.replace(/\D/g, ''));
                    setTotpError(null);
                  }}
                  autoFocus
                  required
                />
                {totpError && (
                  <p className="text-sm text-destructive flex items-center gap-1.5 mt-1">
                    <AlertCircle className="h-4 w-4" />
                    {totpError}
                  </p>
                )}
              </div>
              <Button type="submit" className="w-full" disabled={mfaLoading}>
                {mfaLoading ? 'Verifying...' : 'Verify & Open Control Center'}
              </Button>
              <Button 
                type="button" 
                variant="ghost" 
                className="w-full text-sm text-muted-foreground hover:text-gray-900"
                onClick={handleLogout}
              >
                Sign In as Different Account
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Dashboard workspace
  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl min-h-screen text-left">
      <SEO title="Staff Portal - Dashboard" description="Administrative control center." noindex={true} />
      {/* Header Banner */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-heading text-3xl font-bold text-gray-900">Staff Control Center</h1>
            <Badge variant="default" className="flex items-center gap-1 bg-indigo-600 text-white">
              <Shield className="h-3 w-3" />
              SUPERUSER
            </Badge>
          </div>
          <p className="text-muted-foreground mt-1">
            Logged in as {currentUser.email} • System Administration & Live Analytics
          </p>
        </div>
        <Button variant="destructive" onClick={handleLogout}>
          <LogOut className="h-4 w-4 mr-2" />
          Log Out
        </Button>
      </div>

      {/* Tabs navigation */}
      <div className="flex gap-2 mb-6 border-b pb-px">
        <button
          onClick={() => setActiveTab('users')}
          className={`py-2 px-4 font-semibold text-sm border-b-2 transition-colors ${
            activeTab === 'users'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-gray-900'
          }`}
        >
          Users Directory
        </button>
        <button
          onClick={() => setActiveTab('analytics')}
          className={`py-2 px-4 font-semibold text-sm border-b-2 transition-colors ${
            activeTab === 'analytics'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-gray-900'
          }`}
        >
          Analytics & Traffic
        </button>
        <button
          onClick={() => setActiveTab('anonymous')}
          className={`py-2 px-4 font-semibold text-sm border-b-2 transition-colors ${
            activeTab === 'anonymous'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-gray-900'
          }`}
        >
          Anonymous Creations
        </button>
        <button
          onClick={() => setActiveTab('security')}
          className={`py-2 px-4 font-semibold text-sm border-b-2 transition-colors ${
            activeTab === 'security'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-gray-900'
          }`}
        >
          Security & 2FA
        </button>
        <button
          onClick={() => setActiveTab('blog')}
          className={`py-2 px-4 font-semibold text-sm border-b-2 transition-colors ${
            activeTab === 'blog'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-gray-900'
          }`}
        >
          Manage Blog
        </button>
        <button
          onClick={() => setActiveTab('tickets')}
          className={`py-2 px-4 font-semibold text-sm border-b-2 transition-colors flex items-center gap-1.5 ${
            activeTab === 'tickets'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-gray-900'
          }`}
        >
          Support Tickets
          {tickets.filter(t => t.status === 'open').length > 0 && (
            <Badge variant="destructive" className="px-1.5 py-0.5 text-[10px] leading-none shrink-0 rounded-full bg-red-500 hover:bg-red-500 text-white">
              {tickets.filter(t => t.status === 'open').length}
            </Badge>
          )}
        </button>
      </div>

      {/* VIEW: Users Directory */}
      {activeTab === 'users' && (
        <>
          {/* Metrics Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card className="border border-indigo-100">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Total Accounts</CardTitle>
                <Users className="h-4 w-4 text-indigo-500" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-gray-900">{totalUsers}</div>
                <p className="text-xs text-muted-foreground mt-1">Registered users</p>
              </CardContent>
            </Card>

            <Card className="border border-amber-100 bg-amber-50/20">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Pro Subscriptions</CardTitle>
                <Crown className="h-4 w-4 text-amber-500" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-amber-600">{proUsers}</div>
                <p className="text-xs text-muted-foreground mt-1">Active pro status</p>
              </CardContent>
            </Card>

            <Card className="border border-gray-100">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Free Accounts</CardTitle>
                <UserCheck className="h-4 w-4 text-gray-500" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-gray-700">{freeUsers}</div>
                <p className="text-xs text-muted-foreground mt-1">Remaining users</p>
              </CardContent>
            </Card>

            <Card className="border border-blue-100">
              <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                <CardTitle className="text-sm font-medium">Total Downloads</CardTitle>
                <Download className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-blue-600">
                  {totalDownloads} <span className="text-sm font-normal text-muted-foreground">({todayDownloadsTotal} today)</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Total PDF downloads generated</p>
              </CardContent>
            </Card>
          </div>

          {/* Directory Table */}
          <Card className="shadow-sm">
            <CardHeader className="pb-3 border-b">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                  <CardTitle>User Accounts Directory</CardTitle>
                  <CardDescription>Query profiles, adjust plan statuses, and manage usage counts.</CardDescription>
                </div>
                
                <div className="relative w-full md:w-80">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by email..."
                    className="pl-9"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>
            </CardHeader>
            
            <CardContent className="p-0">
              {loadingUsers ? (
                <div className="py-20 flex justify-center items-center">
                  <RefreshCw className="h-8 w-8 text-primary animate-spin mr-2" />
                  <span>Querying directory...</span>
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="py-20 text-center text-muted-foreground">
                  No matching user profiles found.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left p-4 font-semibold text-sm text-gray-700">User Email</th>
                        <th className="text-left p-4 font-semibold text-sm text-gray-700">Display Name</th>
                        <th className="text-center p-4 font-semibold text-sm text-gray-700">Plan Status</th>
                        <th className="text-center p-4 font-semibold text-sm text-gray-700">Account Status</th>
                        <th className="text-left p-4 font-semibold text-sm text-gray-700">Join Date</th>
                        <th className="text-right p-4 font-semibold text-sm text-gray-700 whitespace-nowrap">Controls</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((userProfile) => (
                        <tr key={userProfile.uid} className="border-b hover:bg-gray-50/50 transition-colors">
                          <td className="p-4 font-medium text-gray-900">{userProfile.email}</td>
                          <td className="p-4 text-gray-600">{userProfile.displayName || '-'}</td>
                          <td className="p-4 text-center">
                            {userProfile.plan === 'pro' ? (
                              <Badge variant="default" className="bg-amber-100 hover:bg-amber-100 text-amber-800 border-amber-200">
                                <Crown className="h-3 w-3 mr-1 text-amber-600" />
                                PRO
                              </Badge>
                            ) : (
                              <Badge variant="secondary">FREE</Badge>
                            )}
                          </td>
                          <td className="p-4 text-center">
                            {userProfile.isBlocked ? (
                              <Badge variant="destructive" className="bg-red-100 hover:bg-red-100 text-red-800 border-red-200 flex items-center gap-1 mx-auto w-fit">
                                <Ban className="h-3 w-3" />
                                Blocked
                              </Badge>
                            ) : (
                              <Badge variant="secondary" className="bg-green-100 hover:bg-green-100 text-green-800 border-green-200 flex items-center gap-1 mx-auto w-fit">
                                <UserCheck className="h-3 w-3 text-green-600" />
                                Active
                              </Badge>
                            )}
                          </td>

                          <td className="p-4 text-sm text-gray-600">
                            {userProfile.createdAt ? format(userProfile.createdAt, 'MMM dd, yyyy') : 'N/A'}
                          </td>
                          <td className="p-4 text-right whitespace-nowrap space-x-1.5">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => handleTogglePlan(userProfile)}
                              title="Toggle between Pro & Free"
                            >
                              {userProfile.plan === 'pro' ? 'Downgrade' : 'Upgrade'}
                            </Button>
                            <Button 
                              variant={userProfile.isBlocked ? "outline" : "destructive"} 
                              size="sm" 
                              onClick={() => handleToggleBlock(userProfile)}
                              title={userProfile.isBlocked ? "Unblock this user account" : "Block this user account"}
                              disabled={userProfile.email.toLowerCase() === 'pathusena@gmail.com'}
                            >
                              {userProfile.isBlocked ? 'Unblock' : 'Block'}
                            </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleViewDetails(userProfile)}
                          title="View user profiles, invoices, businesses"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      </>
      )}

      {/* VIEW: Analytics & Traffic */}
      {activeTab === 'analytics' && (
        <>
          {/* Analytics Header Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card className="border border-indigo-150">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Total Traffic Hits</CardTitle>
                <TrendingUp className="h-4 w-4 text-primary" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-indigo-700">{visits.length}</div>
                <p className="text-xs text-muted-foreground mt-1">Pageviews tracked</p>
              </CardContent>
            </Card>

            <Card className="border border-indigo-100">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Unique Visitors</CardTitle>
                <Globe className="h-4 w-4 text-indigo-500" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-gray-900">{uniqueVisitorsCount}</div>
                <p className="text-xs text-muted-foreground mt-1">Browser cookie identifier</p>
              </CardContent>
            </Card>

            <Card className="border border-emerald-100 bg-emerald-50/10">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Total Invoices Created</CardTitle>
                <FileText className="h-4 w-4 text-emerald-500" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-emerald-600">
                  {statsExtra.invoicesCount + anonymousCreations.filter(c => c.type === 'invoice').length}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {statsExtra.invoicesCount} registered + {anonymousCreations.filter(c => c.type === 'invoice').length} anonymous
                </p>
              </CardContent>
            </Card>

            <Card className="border border-blue-100">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Businesses Created</CardTitle>
                <Building2 className="h-4 w-4 text-blue-500" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-blue-600">{statsExtra.businessesCount}</div>
                <p className="text-xs text-muted-foreground mt-1">Sum across all profiles</p>
              </CardContent>
            </Card>
          </div>

          {/* Landing Page Stats Control Panel */}
          <Card className="mb-8 border border-indigo-150">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-indigo-600" />
                Landing Page Stats Settings
              </CardTitle>
              <CardDescription>
                Configure whether to display actual system counts or custom fake metrics on the landing page.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingStatsSettings ? (
                <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  <span>Loading landing page settings...</span>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="flex items-center justify-between border-b pb-4">
                    <div className="space-y-0.5">
                      <Label htmlFor="fake-stats-toggle" className="text-base font-semibold">
                        Use Fake Analytics Counts
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        When enabled, the landing page will show the custom numbers specified below instead of actual counts.
                      </p>
                    </div>
                    <Switch
                      id="fake-stats-toggle"
                      checked={showFakeStats}
                      onCheckedChange={(checked) => {
                        setShowFakeStats(checked);
                        saveStatsSettings(checked, fakeInvoicesCount, fakeUsersCount, fakeCountriesCount, homePageVersion);
                      }}
                      disabled={savingStatsSettings}
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="fake-invoices">Fake Invoices Created Count</Label>
                      <Input
                        id="fake-invoices"
                        type="number"
                        min="0"
                        value={fakeInvoicesCount}
                        onChange={(e) => setFakeInvoicesCount(Number(e.target.value))}
                        disabled={!showFakeStats || savingStatsSettings}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="fake-users">Fake Active Users Count</Label>
                      <Input
                        id="fake-users"
                        type="number"
                        min="0"
                        value={fakeUsersCount}
                        onChange={(e) => setFakeUsersCount(Number(e.target.value))}
                        disabled={!showFakeStats || savingStatsSettings}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="fake-countries">Fake Countries Count</Label>
                      <Input
                        id="fake-countries"
                        type="number"
                        min="0"
                        value={fakeCountriesCount}
                        onChange={(e) => setFakeCountriesCount(Number(e.target.value))}
                        disabled={!showFakeStats || savingStatsSettings}
                      />
                    </div>
                  </div>

                  {showFakeStats && (
                    <div className="flex justify-end">
                      <Button
                        onClick={() => saveStatsSettings(showFakeStats, fakeInvoicesCount, fakeUsersCount, fakeCountriesCount, homePageVersion)}
                        disabled={savingStatsSettings}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white"
                      >
                        {savingStatsSettings && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Save Stats Numbers
                      </Button>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row sm:items-center justify-between border-t pt-6 gap-4">
                    <div className="space-y-0.5">
                      <Label htmlFor="home-version" className="text-base font-semibold">
                        Serving Home Page Version
                      </Label>
                      <p className="text-sm text-muted-foreground font-normal">
                        Select which version of the homepage is presented to visitors.
                      </p>
                    </div>
                    <Select
                      value={homePageVersion}
                      onValueChange={(val: 'v1' | 'v2') => {
                        setHomePageVersion(val);
                        saveStatsSettings(showFakeStats, fakeInvoicesCount, fakeUsersCount, fakeCountriesCount, val);
                      }}
                      disabled={savingStatsSettings}
                    >
                      <SelectTrigger className="w-48">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="v1">Version 1 (Hero Fold)</SelectItem>
                        <SelectItem value="v2">Version 2 (Interactive Editor)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {loadingAnalytics ? (
            <div className="py-20 flex justify-center items-center">
              <RefreshCw className="h-8 w-8 text-primary animate-spin mr-2" />
              <span>Analyzing live visitor records...</span>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column: Aggregations */}
              <div className="space-y-8">
                
                {/* Frequent Users */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Users className="h-5 w-5 text-indigo-500" />
                      Most Active Logged-In Users
                    </CardTitle>
                    <CardDescription>Frequent users by visit activity.</CardDescription>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b">
                          <tr>
                            <th className="text-left p-3 font-semibold">User Email</th>
                            <th className="text-center p-3 font-semibold w-32">Pageviews</th>
                          </tr>
                        </thead>
                        <tbody>
                          {topFrequentUsers.slice(0, 5).map(({ value, count }) => (
                            <tr key={value} className="border-b">
                              <td className="p-3 font-medium text-gray-900">{value}</td>
                              <td className="p-3 text-center font-bold text-indigo-600">{count}</td>
                            </tr>
                          ))}
                          {topFrequentUsers.length === 0 && (
                            <tr>
                              <td colSpan={2} className="p-4 text-center text-muted-foreground">
                                No registered user hits logged yet.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>

                {/* Country breakdown */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Globe className="h-5 w-5 text-indigo-500" />
                      Geographic Visitor Origins
                    </CardTitle>
                    <CardDescription>Unique countries visits originated from.</CardDescription>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b">
                          <tr>
                            <th className="text-left p-3 font-semibold">Country</th>
                            <th className="text-center p-3 font-semibold w-24">Hits</th>
                            <th className="text-right p-3 font-semibold w-32">Percentage</th>
                          </tr>
                        </thead>
                        <tbody>
                          {topCountries.slice(0, 10).map(({ value, count }) => {
                            const percent = ((count / visits.length) * 100).toFixed(1);
                            return (
                              <tr key={value} className="border-b">
                                <td className="p-3 font-medium">{value}</td>
                                <td className="p-3 text-center">{count}</td>
                                <td className="p-3 text-right text-muted-foreground">{percent}%</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>

                {/* Referrers */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Navigation className="h-5 w-5 text-indigo-500" />
                      Traffic Origins (Referrers)
                    </CardTitle>
                    <CardDescription>Where visitors navigated to the site from.</CardDescription>
                  </CardHeader>
                  <CardContent className="p-0">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="text-left p-3 font-semibold">Origin URL / Referrer</th>
                          <th className="text-center p-3 font-semibold w-32">Hits</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topReferrers.slice(0, 5).map(({ value, count }) => (
                          <tr key={value} className="border-b">
                            <td className="p-3 font-medium text-gray-700 truncate max-w-xs" title={value}>
                              {value === 'Direct' ? <Badge variant="secondary">Direct / Bookmark</Badge> : value}
                            </td>
                            <td className="p-3 text-center">{count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>

                {/* Browser & OS Breakdown */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader className="py-4">
                      <CardTitle className="text-base flex items-center gap-1.5">
                        <Monitor className="h-4.5 w-4.5 text-indigo-500" />
                        Browsers
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0 pb-2">
                      <table className="w-full text-xs">
                        <tbody>
                          {topBrowsers.map(({ value, count }) => (
                            <tr key={value} className="border-b">
                              <td className="p-3 font-medium">{value}</td>
                              <td className="p-3 text-right font-bold">{count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="py-4">
                      <CardTitle className="text-base flex items-center gap-1.5">
                        <Monitor className="h-4.5 w-4.5 text-indigo-500" />
                        Operating Systems
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0 pb-2">
                      <table className="w-full text-xs">
                        <tbody>
                          {topOS.map(({ value, count }) => (
                            <tr key={value} className="border-b">
                              <td className="p-3 font-medium">{value}</td>
                              <td className="p-3 text-right font-bold">{count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </CardContent>
                  </Card>
                </div>

              </div>

              {/* Right Column: Live Timeline */}
              <div>
                <Card className="flex flex-col h-[700px] shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Activity className="h-5 w-5 text-indigo-500 animate-pulse" />
                      Live Visitor Activity Logs
                    </CardTitle>
                    <CardDescription>Timeline of page navigation hits in real time.</CardDescription>
                  </CardHeader>
                  <CardContent className="p-0 flex-1 min-h-0 overflow-hidden flex flex-col">
                    <div className="flex-1 overflow-y-auto pr-1 text-sm">
                      {visits.slice(0, 30).map((v) => (
                        <div key={v.id} className="p-4 border-b hover:bg-gray-50/10 transition-colors">
                          <div className="flex justify-between items-start gap-2 mb-1.5">
                            <div>
                              {v.userEmail ? (
                                <span className="font-semibold text-primary">{v.userEmail}</span>
                              ) : (
                                <Badge variant="secondary" className="text-[10px] py-0">Anonymous Visitor</Badge>
                              )}
                              <span className="text-xs text-muted-foreground ml-2">({v.visitorId.slice(0, 10)}...)</span>
                            </div>
                            <span className="text-xs text-muted-foreground whitespace-nowrap">
                              {format(v.timestamp, 'MMM dd, HH:mm:ss')}
                            </span>
                          </div>
                          <div className="space-y-1 text-xs text-muted-foreground">
                            <p>
                              <span className="font-semibold text-gray-700">Visited page: </span>
                              <code className="bg-muted px-1.5 py-0.5 rounded text-indigo-600 font-mono">{v.path}</code>
                            </p>
                            <p>
                              <span className="font-semibold text-gray-700">Country: </span>
                              <span>{v.country}</span>
                            </p>
                            <p>
                              <span className="font-semibold text-gray-700">Referrer: </span>
                              <span className="truncate max-w-xs">{v.referrer}</span>
                            </p>
                            <p>
                              <span className="font-semibold text-gray-700">System: </span>
                              <span>{v.browser} ({v.os})</span>
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

            </div>
          )}
        </>
      )}

      {/* VIEW: Anonymous Creations */}
      {activeTab === 'anonymous' && (
        <>
          {/* Anonymous Header Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card className="border border-indigo-150">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Anonymous Creations</CardTitle>
                <Activity className="h-4 w-4 text-indigo-500" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-indigo-700">{anonymousCreations.length}</div>
                <p className="text-xs text-muted-foreground mt-1">Invoices + Quotations</p>
              </CardContent>
            </Card>

            <Card className="border border-indigo-100">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Anonymous Invoices</CardTitle>
                <FileText className="h-4 w-4 text-emerald-500" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-gray-900">
                  {anonymousCreations.filter(c => c.type === 'invoice').length}
                </div>
                <p className="text-xs text-muted-foreground mt-1">On-the-fly invoices</p>
              </CardContent>
            </Card>

            <Card className="border border-indigo-100">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Anonymous Quotations</CardTitle>
                <FileText className="h-4 w-4 text-blue-500" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-gray-900">
                  {anonymousCreations.filter(c => c.type === 'quotation').length}
                </div>
                <p className="text-xs text-muted-foreground mt-1">On-the-fly quotes</p>
              </CardContent>
            </Card>

            <Card className="border border-indigo-100">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Unique Visitors</CardTitle>
                <Globe className="h-4 w-4 text-indigo-500" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-gray-900">
                  {new Set(anonymousCreations.map(c => c.visitorId)).size}
                </div>
                <p className="text-xs text-muted-foreground mt-1">Unique anonymous cookie IDs</p>
              </CardContent>
            </Card>
          </div>

          {loadingAnonymous ? (
            <div className="py-20 flex justify-center items-center">
              <RefreshCw className="h-8 w-8 text-primary animate-spin mr-2" />
              <span>Analyzing anonymous creations...</span>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column: Aggregations */}
              <div className="space-y-8">
                {/* Frequent Anonymous Users */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Users className="h-5 w-5 text-indigo-500" />
                      Most Active Anonymous Users
                    </CardTitle>
                    <CardDescription>Grouped by unique anonymous visitor ID.</CardDescription>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b">
                          <tr>
                            <th className="text-left p-3 font-semibold">Visitor ID</th>
                            <th className="text-center p-3 font-semibold w-24">Creations</th>
                            <th className="text-left p-3 font-semibold">Country</th>
                            <th className="text-right p-3 font-semibold">Last Active</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getFrequentAnonymousUsers().slice(0, 10).map((user) => (
                            <tr key={user.visitorId} className="border-b">
                              <td className="p-3 font-mono text-xs font-semibold text-gray-800" title={user.visitorId}>
                                {user.visitorId.slice(0, 12)}...
                              </td>
                              <td className="p-3 text-center font-bold text-indigo-600">{user.count}</td>
                              <td className="p-3 text-left">{user.country}</td>
                              <td className="p-3 text-right text-xs text-muted-foreground">
                                {format(user.lastActive, 'MMM dd, HH:mm')}
                              </td>
                            </tr>
                          ))}
                          {getFrequentAnonymousUsers().length === 0 && (
                            <tr>
                              <td colSpan={4} className="p-4 text-center text-muted-foreground">
                                No anonymous creations logged yet.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Right Column: Live Creations Logs */}
              <div>
                <Card className="flex flex-col h-[600px] shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Activity className="h-5 w-5 text-indigo-500 animate-pulse" />
                      Anonymous Activity Timeline
                    </CardTitle>
                    <CardDescription>Timeline of anonymous downloads and prints.</CardDescription>
                  </CardHeader>
                  <CardContent className="p-0 flex-1 min-h-0 overflow-hidden flex flex-col">
                    <div className="flex-1 overflow-y-auto pr-1 text-sm">
                      {anonymousCreations.slice(0, 30).map((c) => (
                        <div key={c.id} className="p-4 border-b hover:bg-gray-50/10 transition-colors">
                          <div className="flex justify-between items-start gap-2 mb-1.5">
                            <div>
                              <Badge variant="secondary" className="text-[10px] py-0 capitalize bg-slate-100 border border-slate-200">
                                {c.type} {c.action}ed
                              </Badge>
                              <span className="text-xs text-muted-foreground ml-2 font-mono">({c.visitorId.slice(0, 10)}...)</span>
                            </div>
                            <span className="text-xs text-muted-foreground whitespace-nowrap">
                              {format(c.timestamp, 'MMM dd, HH:mm:ss')}
                            </span>
                          </div>
                          <div className="space-y-1 text-xs text-muted-foreground">
                            <p>
                              <span className="font-semibold text-gray-700">Amount: </span>
                              <span className="font-bold text-slate-800">{formatCurrency(c.totalAmount, c.currency)}</span>
                            </p>
                            <p>
                              <span className="font-semibold text-gray-700">Country: </span>
                              <span>{c.country}</span>
                            </p>
                            <p>
                              <span className="font-semibold text-gray-700">System: </span>
                              <span>{c.browser} ({c.os})</span>
                            </p>
                          </div>
                        </div>
                      ))}
                      {anonymousCreations.length === 0 && (
                        <div className="py-20 text-center text-muted-foreground italic">
                          No anonymous creation logs found.
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </>
      )}

      {/* VIEW: Security & 2FA */}
      {activeTab === 'security' && (
        <Card className="max-w-2xl mx-auto shadow-sm">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Shield className="h-6 w-6 text-indigo-600" />
              <CardTitle>Two-Factor Authentication (2FA)</CardTitle>
            </div>
            <CardDescription>
              Secure your administrative access using time-based one-time passcodes (TOTP) from an authenticator app like Google Authenticator or Microsoft Authenticator.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {mfaEnabled ? (
              <div className="border border-green-200 bg-green-50/20 rounded-lg p-6 space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 text-green-600 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-green-900 text-lg">Google Authenticator is Enabled</h3>
                    <p className="text-sm text-green-700 mt-1">
                      Your admin portal is secured. Every time you sign in to your staff portal in a new session, you will be required to provide the 6-digit validation code.
                    </p>
                  </div>
                </div>
                <div className="pt-2">
                  <Button 
                    variant="destructive" 
                    onClick={handleDisableMfa} 
                    disabled={mfaLoading}
                  >
                    Disable Two-Factor Authentication
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {setupStep === 'idle' ? (
                  <div className="border border-gray-150 rounded-lg p-6 space-y-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-6 w-6 text-amber-500 mt-0.5" />
                      <div>
                        <h3 className="font-semibold text-gray-900 text-lg">Two-Factor Authentication is Disabled</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          Protect your account by adding an extra layer of security. To enable 2FA, click the button below to start the setup wizard.
                        </p>
                      </div>
                    </div>
                    <div className="pt-2">
                      <Button onClick={handleStartMfaSetup} disabled={mfaLoading}>
                        Start 2FA Setup
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="border border-indigo-100 bg-indigo-50/10 rounded-lg p-6 space-y-6">
                    <h3 className="font-semibold text-gray-900 text-lg border-b pb-2">
                      Enable Google Authenticator (Step {setupStep === 'scan' ? '1: Scan QR Code' : '2: Verify Code'})
                    </h3>

                    {setupStep === 'scan' && (
                      <div className="flex flex-col md:flex-row items-center gap-6">
                        <div className="bg-white p-3 border rounded-lg shadow-sm">
                          {mfaSecret && (
                            <img 
                              src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
                                `otpauth://totp/QuickFreeInvoice:pathusena@gmail.com?secret=${mfaSecret}&issuer=QuickFreeInvoice`
                              )}`}
                              alt="Scan to add TOTP"
                              className="h-44 w-44"
                            />
                          )}
                        </div>
                        <div className="space-y-3 flex-1 text-sm text-gray-600">
                          <p className="font-medium text-gray-900">Follow these steps:</p>
                          <ol className="list-decimal list-inside space-y-2 pl-1">
                            <li>Open your authenticator app (e.g., Google Authenticator).</li>
                            <li>Scan the QR code shown here, or manually add a key.</li>
                            {mfaSecret && (
                              <li>
                                Manual Key: <code className="bg-gray-100 px-1.5 py-0.5 rounded font-mono font-bold text-indigo-700 select-all">{mfaSecret}</code>
                              </li>
                            )}
                          </ol>
                          <div className="pt-2">
                            <Button onClick={() => setSetupStep('verify')}>
                              Next: Verify Code
                            </Button>
                            <Button variant="ghost" className="ml-2" onClick={handleCancelSetup}>
                              Cancel
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}

                    {setupStep === 'verify' && (
                      <div className="space-y-4 max-w-sm">
                        <div className="space-y-2">
                          <Label htmlFor="setupCode">Enter 6-Digit Code</Label>
                          <Input
                            id="setupCode"
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            maxLength={6}
                            placeholder="000000"
                            className="text-center font-mono text-xl tracking-widest"
                            value={totpCode}
                            onChange={(e) => {
                              setTotpCode(e.target.value.replace(/\D/g, ''));
                              setTotpError(null);
                            }}
                            autoFocus
                            required
                          />
                          {totpError && (
                            <p className="text-sm text-destructive flex items-center gap-1 mt-1">
                              <AlertCircle className="h-4 w-4" />
                              {totpError}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button onClick={handleConfirmMfaSetup} disabled={mfaLoading}>
                            {mfaLoading ? 'Activating...' : 'Confirm & Enable 2FA'}
                          </Button>
                          <Button variant="outline" onClick={() => { setSetupStep('scan'); setTotpCode(''); }}>
                            Back
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* VIEW: Blog Management */}
      {activeTab === 'blog' && (
        <Card className="shadow-sm">
          <CardHeader className="pb-3 border-b">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <CardTitle className="text-xl font-bold flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-indigo-500" />
                  Manage Blog Posts
                </CardTitle>
                <CardDescription>Create, edit, and delete your blog posts and cover images.</CardDescription>
              </div>
              <Button onClick={handleCreateNewPost}>
                <Plus className="h-4 w-4 mr-2" />
                Create New Post
              </Button>
            </div>
          </CardHeader>
          
          <CardContent className="p-0">
            {loadingBlog ? (
              <div className="py-20 flex justify-center items-center">
                <RefreshCw className="h-8 w-8 text-primary animate-spin mr-2" />
                <span>Loading blog posts...</span>
              </div>
            ) : blogPosts.length === 0 ? (
              <div className="py-20 text-center text-muted-foreground">
                No blog posts found. Click "Create New Post" to get started.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="p-4 font-semibold text-sm text-gray-700 w-24">Cover</th>
                      <th className="p-4 font-semibold text-sm text-gray-700">Title & Slug</th>
                      <th className="p-4 font-semibold text-sm text-gray-700">Category</th>
                      <th className="p-4 font-semibold text-sm text-gray-700">Date</th>
                      <th className="p-4 font-semibold text-sm text-gray-700">Read Time</th>
                      <th className="p-4 font-semibold text-sm text-gray-700 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {blogPosts.map((post) => (
                      <tr key={post.id} className="border-b hover:bg-gray-50/50 transition-colors">
                        <td className="p-4">
                          {post.image ? (
                            <img 
                              src={getSafeBlogImageUrl(post.image)} 
                              alt={post.title} 
                              className="h-10 w-16 object-cover rounded border"
                            />
                          ) : (
                            <div className="h-10 w-16 bg-gray-100 rounded border flex items-center justify-center">
                              <BookOpen className="h-4 w-4 text-muted-foreground" />
                            </div>
                          )}
                        </td>
                        <td className="p-4">
                          <div className="font-semibold text-gray-900 line-clamp-1">{post.title}</div>
                          <div className="text-xs text-muted-foreground font-mono">/blog/{post.slug}</div>
                        </td>
                        <td className="p-4">
                          <Badge variant="secondary">{post.category}</Badge>
                        </td>
                        <td className="p-4 text-sm text-gray-600">
                          {post.date}
                        </td>
                        <td className="p-4 text-sm text-gray-600">
                          {post.readTime}
                        </td>
                        <td className="p-4 text-right space-x-1">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => handleEditPost(post)}
                            title="Edit blog post"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="destructive" 
                            size="sm" 
                            onClick={() => handleDeleteBlogPost(post.id)}
                            title="Delete blog post"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* VIEW: Support Tickets */}
      {activeTab === 'tickets' && (
        <Card className="shadow-lg border-slate-100">
          <CardHeader className="border-b pb-4 mb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <CardTitle className="text-xl font-bold flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-indigo-600" />
                User Support Tickets
              </CardTitle>
              <CardDescription>
                View, reply to, resolve, and close customer issue reports.
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={fetchTickets} disabled={loadingTickets}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loadingTickets ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            <div className="grid grid-cols-1 md:grid-cols-3 min-h-[600px]">
              {/* Left Column: Tickets List */}
              <div className="border-r border-slate-100 flex flex-col max-h-[700px]">
                {/* Search & Filter */}
                <div className="p-4 border-b border-slate-100 space-y-3 shrink-0">
                  <div className="flex gap-1 bg-slate-50 p-1 rounded-lg">
                    {(['all', 'open', 'closed'] as const).map((filter) => (
                      <button
                        key={filter}
                        onClick={() => setStatusFilter(filter)}
                        className={`flex-1 py-1 text-xs font-semibold rounded-md capitalize transition-all ${
                          statusFilter === filter
                            ? 'bg-white text-slate-800 shadow-sm'
                            : 'text-muted-foreground hover:text-slate-800'
                        }`}
                      >
                        {filter}
                      </button>
                    ))}
                  </div>

                  {/* Unified Search for User or Title */}
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 h-3 w-3 text-muted-foreground" />
                    <Input
                      placeholder="Search user email or title..."
                      value={ticketSearchQuery}
                      onChange={(e) => setTicketSearchQuery(e.target.value)}
                      className="pl-7 h-8 text-xs focus-visible:ring-indigo-600 bg-white"
                    />
                    {ticketSearchQuery && (
                      <button
                        type="button"
                        onClick={() => setTicketSearchQuery('')}
                        className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-slate-800"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>

                  {/* Date Filter */}
                  <div className="flex items-center gap-2 border rounded-lg bg-white px-2.5 py-1">
                    <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap">Date:</span>
                    <input
                      type="date"
                      value={ticketDateQuery}
                      onChange={(e) => setTicketDateQuery(e.target.value)}
                      className="flex-1 text-xs bg-transparent border-none outline-none focus:ring-0 text-slate-750 h-6"
                    />
                    {ticketDateQuery && (
                      <button
                        type="button"
                        onClick={() => setTicketDateQuery('')}
                        className="text-muted-foreground hover:text-slate-850 shrink-0"
                        title="Clear date filter"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>

                {/* List Container */}
                <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
                  {loadingTickets ? (
                    <div className="py-20 text-center text-muted-foreground flex flex-col items-center justify-center">
                      <Loader2 className="h-6 w-6 animate-spin mb-2 text-primary" />
                      <span className="text-xs">Loading support tickets...</span>
                    </div>
                  ) : tickets.length === 0 ? (
                    <div className="py-20 text-center text-muted-foreground text-xs italic">
                      No tickets found.
                    </div>
                  ) : (
                    (() => {
                      const filteredTickets = tickets.filter(t => {
                        const matchesStatus = statusFilter === 'all' ? true : t.status === statusFilter;
                        
                        const cleanQuery = ticketSearchQuery.toLowerCase().trim();
                        const matchesText = !cleanQuery ? true : (
                          t.userEmail.toLowerCase().includes(cleanQuery) ||
                          t.title.toLowerCase().includes(cleanQuery)
                        );
                        
                        let matchesDate = true;
                        if (ticketDateQuery) {
                          const ticketDateStr = format(t.createdAt, 'yyyy-MM-dd');
                          matchesDate = ticketDateStr === ticketDateQuery;
                        }
                        
                        return matchesStatus && matchesText && matchesDate;
                      });
                      
                      if (filteredTickets.length === 0) {
                        return (
                          <div className="py-20 text-center text-slate-400 text-xs italic space-y-1">
                            <div>No matching tickets.</div>
                            {(ticketSearchQuery || ticketDateQuery) && (
                              <button
                                onClick={() => {
                                  setTicketSearchQuery('');
                                  setTicketDateQuery('');
                                }}
                                className="text-[10px] text-indigo-600 hover:underline font-semibold"
                              >
                                Clear filters
                              </button>
                            )}
                          </div>
                        );
                      }

                      return filteredTickets.map((t) => (
                        <div
                          key={t.ticketId}
                          onClick={() => {
                            setSelectedTicketId(t.ticketId);
                            setAdminReplyText('');
                          }}
                          className={`p-4 hover:bg-slate-50/50 cursor-pointer transition-colors text-left flex flex-col gap-1.5 ${
                            selectedTicketId === t.ticketId ? 'bg-indigo-50/30 border-l-4 border-indigo-600' : ''
                          }`}
                        >
                          <div className="flex justify-between items-start gap-2">
                            <span className="text-xs text-muted-foreground font-mono truncate max-w-[150px]">
                              {t.userEmail}
                            </span>
                            <Badge
                              variant={t.status === 'open' ? 'default' : 'secondary'}
                              className={`text-[9px] uppercase tracking-wider py-0 px-1.5 ${
                                t.status === 'open' ? 'bg-orange-500 hover:bg-orange-500 text-white' : ''
                              }`}
                            >
                              {t.status}
                            </Badge>
                          </div>
                          <h4 className="font-semibold text-sm text-slate-800 line-clamp-1">{t.title}</h4>
                          <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {format(t.createdAt, 'MMM d, yyyy h:mm a')}
                          </span>
                        </div>
                      ));
                    })()
                  )}
                </div>
              </div>

              {/* Right Column: Ticket Inspector */}
              <div className="col-span-2 flex flex-col max-h-[700px] bg-slate-50/30">
                {currentTicket ? (
                  <div className="flex flex-col h-full">
                    {/* Inspector Header */}
                    <div className="p-4 bg-white border-b border-slate-100 flex justify-between items-center shrink-0">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-base text-slate-800">{currentTicket.title}</h3>
                          <Badge
                            variant={currentTicket.status === 'open' ? 'default' : 'secondary'}
                            className={`text-[10px] uppercase ${
                              currentTicket.status === 'open' ? 'bg-orange-500 hover:bg-orange-500 text-white' : ''
                            }`}
                          >
                            {currentTicket.status}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 font-medium">
                          From: <span className="font-mono text-slate-700">{currentTicket.userEmail}</span> • {format(currentTicket.createdAt, 'MMM d, yyyy h:mm a')}
                        </p>
                      </div>

                      {currentTicket.status === 'open' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleAdminCloseTicket(currentTicket.ticketId)}
                          disabled={ticketsSubmitting}
                          className="text-red-600 hover:text-red-700 border-red-200 hover:bg-red-50 text-xs font-semibold"
                        >
                          Close Ticket
                        </Button>
                      )}
                    </div>

                    {/* Chat History */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                      {/* Original Issue Box */}
                      <div className="p-4 bg-white border border-slate-150 rounded-xl space-y-3 shadow-sm">
                        <div className="space-y-1">
                          <span className="font-bold text-xs text-slate-400 uppercase tracking-wider block">User Description:</span>
                          <p className="text-slate-850 text-sm whitespace-pre-wrap leading-relaxed">
                            {currentTicket.description}
                          </p>
                        </div>
                        {currentTicket.screenshotUrl && (
                          <div className="space-y-1 pt-1">
                            <span className="font-bold text-xs text-slate-400 uppercase tracking-wider block">Attached Screenshot:</span>
                            <button
                              type="button"
                              onClick={() => setLightboxUrl(currentTicket.screenshotUrl || null)}
                              className="inline-block border rounded-lg overflow-hidden max-w-[300px] hover:ring-2 hover:ring-primary transition-all bg-slate-50 text-left focus:outline-none focus:ring-2 focus:ring-primary"
                            >
                              <img
                                src={currentTicket.screenshotUrl}
                                alt="Support screenshot"
                                className="max-w-full max-h-[200px] object-contain cursor-zoom-in"
                              />
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Chat Messages */}
                      <div className="space-y-3 pt-2">
                        {selectedTicketReplies.length === 0 ? (
                          <div className="text-center text-xs text-muted-foreground italic py-4">
                            No replies yet. Type a response below to comment/reply.
                          </div>
                        ) : (
                          selectedTicketReplies.map((reply) => {
                            const isStaffReply = reply.isAdmin;
                            return (
                              <div
                                key={reply.replyId}
                                className={`flex flex-col max-w-[85%] ${
                                  isStaffReply ? 'ml-auto items-end' : 'mr-auto items-start'
                                }`}
                              >
                                <span className="text-[10px] text-muted-foreground mb-0.5 px-1">
                                  {isStaffReply ? '🛡️ You (Staff Support)' : `User (${reply.senderEmail})`}
                                </span>
                                <div
                                  className={`p-3 rounded-2xl text-sm leading-relaxed ${
                                    isStaffReply
                                      ? 'bg-indigo-600 text-white rounded-tr-none shadow-sm'
                                      : 'bg-white text-slate-850 rounded-tl-none border border-slate-150 shadow-sm'
                                  }`}
                                >
                                  <p className="whitespace-pre-wrap text-sm">{reply.text}</p>
                                </div>
                                <span className="text-[9px] text-muted-foreground mt-1 px-1">
                                  {format(reply.createdAt, 'MMM d, h:mm a')}
                                </span>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>

                    {/* Chat Input */}
                    <div className="p-4 bg-white border-t border-slate-100 shrink-0">
                      {currentTicket.status === 'closed' ? (
                        <div className="text-center py-2 text-xs text-muted-foreground italic bg-slate-50 rounded-lg flex items-center justify-center gap-1.5 font-medium">
                          <Lock className="w-3.5 h-3.5" />
                          This ticket is closed. No further replies can be sent.
                        </div>
                      ) : (
                        <form onSubmit={handleAdminSubmitReply} className="flex gap-2 items-end">
                          <Textarea
                            value={adminReplyText}
                            onChange={(e) => setAdminReplyText(e.target.value)}
                            placeholder="Type a response/request for info..."
                            rows={3}
                            className="resize-none text-sm max-h-[120px] rounded-xl focus-visible:ring-indigo-600"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleAdminSubmitReply(e);
                              }
                            }}
                          />
                          <Button
                            type="submit"
                            disabled={ticketsSubmitting || !adminReplyText.trim()}
                            className="h-10 px-4 bg-indigo-600 hover:bg-indigo-700 shrink-0 rounded-xl"
                          >
                            {ticketsSubmitting ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Send className="w-4 h-4" />
                            )}
                          </Button>
                        </form>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground p-8">
                    <MessageSquare className="w-12 h-12 text-slate-200 mb-2" />
                    <p className="text-sm font-medium">Select a ticket from the left panel</p>
                    <p className="text-xs">Or click Refresh to retrieve the latest user requests.</p>
                  </div>
                )}
              </div>
            </div>
            {/* Screenshot Lightbox Dialog */}
            <Dialog open={!!lightboxUrl} onOpenChange={(open) => !open && setLightboxUrl(null)}>
              <DialogContent className="max-w-4xl p-0 bg-transparent border-none shadow-none flex items-center justify-center">
                <div className="relative max-h-[90vh] max-w-full overflow-hidden bg-black/95 rounded-lg flex items-center justify-center p-1 sm:p-2 border border-white/10">
                  <button
                    type="button"
                    onClick={() => setLightboxUrl(null)}
                    className="absolute top-3 right-3 p-1.5 bg-black/60 hover:bg-black/80 text-white rounded-full transition-colors z-50 border border-white/10"
                    title="Close image view"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  {lightboxUrl && (
                    <img
                      src={lightboxUrl}
                      alt="Enlarged screenshot"
                      className="max-h-[85vh] max-w-full object-contain rounded"
                    />
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>
      )}

      {/* Blog Editor Dialog */}
      <Dialog open={showBlogDialog} onOpenChange={setShowBlogDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="border-b pb-4 mb-4">
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-indigo-500" />
              <span>{editingPost ? 'Edit Blog Post' : 'Create New Blog Post'}</span>
            </DialogTitle>
            <DialogDescription>
              Write content in Markdown, add metadata, and upload a cover image.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSaveBlogPost} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="postTitle">Post Title *</Label>
                <Input 
                  id="postTitle" 
                  value={blogForm.title} 
                  onChange={(e) => setBlogForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g. How to Invoicing Freelance Clients"
                  required
                />
              </div>

              {/* Slug */}
              <div className="space-y-2">
                <Label htmlFor="postSlug">Custom Slug (auto-generated if empty)</Label>
                <Input 
                  id="postSlug" 
                  value={blogForm.slug} 
                  onChange={(e) => setBlogForm(prev => ({ ...prev, slug: e.target.value }))}
                  placeholder="e.g. freelance-invoicing-guide"
                />
              </div>

              {/* Category */}
              <div className="space-y-2">
                <Label htmlFor="postCategory">Category</Label>
                <Input 
                  id="postCategory" 
                  value={blogForm.category} 
                  onChange={(e) => setBlogForm(prev => ({ ...prev, category: e.target.value }))}
                  placeholder="e.g. Invoicing, Freelancing, Taxes"
                />
              </div>

              {/* Author */}
              <div className="space-y-2">
                <Label htmlFor="postAuthor">Author</Label>
                <Input 
                  id="postAuthor" 
                  value={blogForm.author} 
                  onChange={(e) => setBlogForm(prev => ({ ...prev, author: e.target.value }))}
                  placeholder="e.g. QuickFreeInvoice Team"
                />
              </div>

              {/* Tags */}
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="postTags">Tags (comma-separated)</Label>
                <Input 
                  id="postTags" 
                  value={blogForm.tagsString} 
                  onChange={(e) => setBlogForm(prev => ({ ...prev, tagsString: e.target.value }))}
                  placeholder="e.g. freelance, payments, invoicing, smallbusiness"
                />
              </div>

              {/* Excerpt */}
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="postExcerpt">Excerpt / Short Description *</Label>
                <Input 
                  id="postExcerpt" 
                  value={blogForm.excerpt} 
                  onChange={(e) => setBlogForm(prev => ({ ...prev, excerpt: e.target.value }))}
                  placeholder="A short one-sentence preview of the article content."
                  required
                />
              </div>

              {/* Cover Image Upload */}
              <div className="space-y-2 md:col-span-2">
                <Label>Cover Image</Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                  <div className="md:col-span-2">
                    <div className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:bg-gray-50 transition-colors relative">
                      <input 
                        type="file" 
                        accept="image/*"
                        onChange={handleImageChange}
                        className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                      />
                      <div className="flex flex-col items-center justify-center gap-1.5 text-muted-foreground">
                        <Upload className="h-6 w-6 text-indigo-500" />
                        <span className="text-sm font-semibold text-gray-800">
                          {imageUploading ? 'Uploading and compressing...' : 'Click or Drag cover image here'}
                        </span>
                        <span className="text-xs text-gray-500">PNG, JPG, WebP up to 5MB (auto-compressed to 800px)</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-center border rounded-lg p-2 bg-gray-50/50 min-h-[110px] items-center relative overflow-hidden">
                    {blogForm.image ? (
                      <>
                        <img 
                          src={getSafeBlogImageUrl(blogForm.image)} 
                          alt="Cover preview" 
                          className="max-h-24 max-w-full object-contain rounded"
                        />
                        <Button 
                          type="button" 
                          variant="destructive" 
                          size="sm" 
                          className="absolute bottom-1 right-1 h-6 px-2 text-xs"
                          onClick={() => setBlogForm(prev => ({ ...prev, image: '' }))}
                        >
                          Remove
                        </Button>
                      </>
                    ) : (
                      <div className="text-xs text-muted-foreground flex flex-col items-center gap-1">
                        <ImageIcon className="h-6 w-6 text-gray-300" />
                        <span>No cover image selected</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Markdown Content & Preview */}
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="postContent">Article Content (Markdown) *</Label>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <textarea 
                    id="postContent"
                    value={blogForm.content}
                    onChange={(e) => setBlogForm(prev => ({ ...prev, content: e.target.value }))}
                    placeholder="Write article content using Markdown format..."
                    rows={15}
                    className="w-full p-3 border rounded-lg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white resize-y"
                    required
                  />
                  <div className="border rounded-lg bg-gray-50/50 p-4 h-[330px] lg:h-auto overflow-y-auto max-h-[400px]">
                    <div className="text-xs font-semibold text-muted-foreground border-b pb-1.5 mb-3 uppercase tracking-wider">Live Preview</div>
                    {blogForm.content ? (
                      <div className="prose prose-sm max-w-none text-left">
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            h1: ({ node, ...props }) => <h1 className="text-xl font-bold mb-3 mt-4" {...props} />,
                            h2: ({ node, ...props }) => <h2 className="text-lg font-bold mb-2 mt-3" {...props} />,
                            h3: ({ node, ...props }) => <h3 className="text-base font-semibold mb-1.5 mt-2" {...props} />,
                            p: ({ node, ...props }) => <p className="mb-3 leading-relaxed text-gray-700 text-sm" {...props} />,
                            ul: ({ node, ...props }) => <ul className="list-disc pl-5 mb-3 space-y-1 text-sm" {...props} />,
                            ol: ({ node, ...props }) => <ol className="list-decimal pl-5 mb-3 space-y-1 text-sm" {...props} />,
                            li: ({ node, ...props }) => <li className="text-gray-700 text-sm" {...props} />,
                            strong: ({ node, ...props }) => <strong className="font-semibold text-gray-900" {...props} />,
                            code: ({ node, ...props }) => <code className="bg-gray-250/60 px-1.5 py-0.5 rounded text-xs font-mono" {...props} />,
                            pre: ({ node, ...props }) => <pre className="bg-gray-150 p-3 rounded-lg overflow-x-auto mb-3 text-xs" {...props} />,
                            blockquote: ({ node, ...props }) => <blockquote className="border-l-4 border-indigo-500 pl-3 italic my-3 text-gray-600 text-sm" {...props} />,
                            table: ({ node, ...props }) => (
                              <div className="overflow-x-auto my-4 border border-gray-200 rounded-lg shadow-sm">
                                <table className="min-w-full divide-y divide-gray-200 text-xs" {...props} />
                              </div>
                            ),
                            thead: ({ node, ...props }) => <thead className="bg-gray-50 font-semibold text-gray-900 border-b border-gray-200" {...props} />,
                            tbody: ({ node, ...props }) => <tbody className="divide-y divide-gray-200 bg-white" {...props} />,
                            tr: ({ node, ...props }) => <tr className="hover:bg-gray-50/50 transition-colors" {...props} />,
                            th: ({ node, ...props }) => <th className="px-3 py-2 text-left font-semibold text-gray-900" {...props} />,
                            td: ({ node, ...props }) => <td className="px-3 py-2 text-gray-700 align-middle" {...props} />,
                          }}
                        >
                          {blogForm.content}
                        </ReactMarkdown>
                      </div>
                    ) : (
                      <div className="text-sm text-muted-foreground italic text-center py-10">
                        Markdown preview will appear here as you type.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter className="border-t pt-4 mt-6">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowBlogDialog(false)}
                disabled={loadingBlogSave}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loadingBlogSave}>
                {loadingBlogSave ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Post'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* User Details Inspector Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          {loadingDetails ? (
            <div className="py-20 flex flex-col justify-center items-center gap-2">
              <RefreshCw className="h-8 w-8 text-primary animate-spin" />
              <span>Fetching user records...</span>
            </div>
          ) : selectedUser ? (
            <div>
              <DialogHeader className="border-b pb-4 mb-4">
                <DialogTitle className="text-xl font-bold flex items-center gap-2">
                  <span>Inspector Profile: {selectedUser.email}</span>
                  {selectedUser.plan === 'pro' ? (
                    <Badge className="bg-amber-100 text-amber-800 border border-amber-200">PRO</Badge>
                  ) : (
                    <Badge variant="secondary">FREE</Badge>
                  )}
                </DialogTitle>
                <DialogDescription>
                  Detailed audit of profiles, invoices, and businesses for UID: {selectedUser.uid}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6">
                
                {/* Account details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-muted/30 p-4 rounded-lg border text-sm">
                  <div>
                    <span className="font-semibold text-gray-600 block">UID</span>
                    <span className="font-mono text-gray-900 select-all block">{selectedUser.uid}</span>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-600 block">Registered On</span>
                    <span className="text-gray-900 block">
                      {selectedUser.createdAt ? format(selectedUser.createdAt, 'MMMM dd, yyyy HH:mm:ss') : 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-600 block">Total Downloads</span>
                    <span className="text-gray-900 block">
                      {selectedUser.downloadCount ?? 0} total
                    </span>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-600 block">Subscription status</span>
                    <span className="text-gray-900 block">
                      {selectedUser.subscription?.status || 'none'} 
                      {selectedUser.subscription?.provider ? ` (via ${selectedUser.subscription.provider})` : ''}
                    </span>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-600 block">Account Status</span>
                    <span className="text-gray-900 block mt-0.5">
                      {selectedUser.isBlocked ? (
                        <Badge variant="destructive" className="bg-red-100 hover:bg-red-100 text-red-800 border-red-200 flex items-center gap-1 w-fit">
                          <Ban className="h-3 w-3" />
                          Blocked
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-green-100 hover:bg-green-100 text-green-800 border-green-200 flex items-center gap-1 w-fit">
                          <UserCheck className="h-3 w-3 text-green-600" />
                          Active
                        </Badge>
                      )}
                    </span>
                  </div>
                </div>

                {/* Invoices List */}
                <div>
                  <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2 mb-3">
                    <FileText className="h-5 w-5 text-indigo-500" />
                    Invoices ({selectedUserInvoices.length})
                  </h3>
                  {selectedUserInvoices.length === 0 ? (
                    <p className="text-sm text-muted-foreground bg-gray-50 p-4 rounded border">
                      This user has not created any invoices yet.
                    </p>
                  ) : (
                    <div className="border rounded-lg overflow-hidden max-h-60 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b">
                          <tr>
                            <th className="text-left p-3 font-semibold text-gray-700">Invoice #</th>
                            <th className="text-left p-3 font-semibold text-gray-700">Client</th>
                            <th className="text-left p-3 font-semibold text-gray-700">Issue Date</th>
                            <th className="text-center p-3 font-semibold text-gray-700">Status</th>
                            <th className="text-right p-3 font-semibold text-gray-700">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedUserInvoices.map((inv) => (
                            <tr key={inv.invoiceId} className="border-b hover:bg-gray-50/50">
                              <td className="p-3 font-medium text-gray-900">{inv.invoiceNumber}</td>
                              <td className="p-3 text-gray-600">
                                {inv.billTo?.name || inv.billTo?.companyName || 'No client'}
                              </td>
                              <td className="p-3 text-gray-600">
                                {inv.issueDate ? format(inv.issueDate, 'MMM dd, yyyy') : 'N/A'}
                              </td>
                              <td className="p-3 text-center flex justify-center">
                                {getInvoiceStatusBadge(inv.status)}
                              </td>
                              <td className="p-3 text-right font-semibold">
                                {formatCurrency(inv.totalAmount || 0, inv.currency)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Businesses & Customers Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2 mb-3">
                      <Building2 className="h-5 w-5 text-indigo-500" />
                      Businesses ({selectedUserBusinesses.length})
                    </h3>
                    {selectedUserBusinesses.length === 0 ? (
                      <p className="text-sm text-muted-foreground bg-gray-50 p-4 rounded border">
                        No saved business profiles.
                      </p>
                    ) : (
                      <div className="space-y-3 max-h-60 overflow-y-auto pr-1">
                        {selectedUserBusinesses.map((bus) => (
                          <div key={bus.businessId} className="p-3 border rounded-lg bg-gray-50/50 text-sm">
                            <div className="flex items-center justify-between mb-1">
                              <h4 className="font-semibold text-gray-900">{bus.name}</h4>
                              {bus.isDefault && <Badge variant="secondary" className="text-[10px] py-0 px-1.5">Default</Badge>}
                            </div>
                            <p className="text-gray-600 text-xs mt-0.5">{bus.email} • {bus.phone || 'No phone'}</p>
                            {bus.address && <p className="text-gray-500 text-xs mt-1 whitespace-pre-line border-t pt-1 border-gray-200/50">{bus.address}</p>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2 mb-3">
                      <Briefcase className="h-5 w-5 text-indigo-500" />
                      Customers ({selectedUserCustomers.length})
                    </h3>
                    {selectedUserCustomers.length === 0 ? (
                      <p className="text-sm text-muted-foreground bg-gray-50 p-4 rounded border">
                        No saved customer profiles.
                      </p>
                    ) : (
                      <div className="space-y-3 max-h-60 overflow-y-auto pr-1">
                        {selectedUserCustomers.map((cust) => (
                          <div key={cust.customerId} className="p-3 border rounded-lg bg-gray-50/50 text-sm">
                            <div className="flex items-center justify-between mb-1">
                              <h4 className="font-semibold text-gray-900">{cust.name}</h4>
                              {cust.isDefault && <Badge variant="secondary" className="text-[10px] py-0 px-1.5">Default</Badge>}
                            </div>
                            <p className="text-gray-600 text-xs mt-0.5">
                              {cust.companyName ? `${cust.companyName} • ` : ''}{cust.email || 'No email'}
                            </p>
                            {cust.address && <p className="text-gray-500 text-xs mt-1 whitespace-pre-line border-t pt-1 border-gray-200/50">{cust.address}</p>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

              </div>

              <DialogFooter className="mt-6 border-t pt-4 flex flex-row justify-between items-center gap-2">
                {selectedUser.email.toLowerCase() !== 'pathusena@gmail.com' ? (
                  <Button 
                    variant={selectedUser.isBlocked ? "outline" : "destructive"} 
                    onClick={() => handleToggleBlock(selectedUser)}
                    size="sm"
                  >
                    {selectedUser.isBlocked ? 'Unblock User' : 'Block User'}
                  </Button>
                ) : <div />}
                <Button onClick={() => setShowDetailDialog(false)} size="sm">
                  Close Audit
                </Button>
              </DialogFooter>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
